/**
 * Composed-index slot addressing — resolving a (mount, slot) pair against the composed mount list
 * and writing back through it. Shared by the weapon, mod, and retrofit handler groups.
 *
 * Split out of `../reducer`; see it for the tier's contract.
 */

import { CommandError, err, ok, type Result } from "@lancer-vtt/shared";
import { Compendium } from "@lancer-vtt/compendium";
import { composedMountProvenance, composedMounts, type BonusMountProvenance } from "../composedMounts";
import { slotSizesForMount } from "../mounts";
import type { LoadoutData, MountData, PilotData, SlotData } from "../types";

// ── composed-index addressing ──────────────────────────────────────────────────
//
// `equip_weapon`/`unequip_weapon`/`attach_mod`/`detach_mod` all address the
// **composed** mount array (`composedMounts`), never `loadout.mounts` directly —
// native mounts are the stable index prefix (`index < loadout.mounts.length`);
// bonus mounts, when composed in, sit at the tail (`index >=
// loadout.mounts.length`) in the pinned order Improved Armament then Integrated
// Weapon. A composed index the pilot's CURRENT `composedMounts()` doesn't produce
// (no native mount there, or a bonus mount the pilot doesn't hold / is over its
// cap for) naturally has no `mount`/`slot` to resolve, so `requireComposedSlot`
// rejects it — this doubles as the `<3` native-mount cap enforcement at equip
// time; callers must not re-derive that cap separately. `composedMountProvenance`
// (`composedMounts.ts`) disambiguates WHICH bonus mount a tail index is — used by
// `writeComposedSlot` to route a write to the right dedicated storage field, and
// by `attach_mod`'s `no_mods` gate.

/** Named arguments for {@link requireComposedSlot}. */
export type RequireComposedSlotArgs = {
  loadout: LoadoutData;
  pilot: PilotData;
  compendium: Compendium;
  mountIdx: number;
  slotIdx: number;
  /** The calling command's name, folded into the rejection message (e.g. "equip_weapon"). */
  command: string;
}

/**
 * Resolve a composed-index mount/slot for a READ (validation). Returns the
 * composed array and its parallel provenance too (the write path needs `composed`
 * to preserve a bonus mount's other slot(s) when only one slot changes, and
 * `provenance` to route a tail write to the right storage field) alongside the
 * resolved `mount`/`slot`.
 */
export function requireComposedSlot(
  args: RequireComposedSlotArgs,
): Result<
  {
    composed: MountData[];
    provenance: (BonusMountProvenance | null)[];
    mount: MountData;
    slot: SlotData;
  },
  CommandError
> {
  const { loadout, pilot, compendium, mountIdx, slotIdx, command } = args;
  const composed = composedMounts(loadout, pilot, compendium);
  const provenance = composedMountProvenance(loadout, pilot, compendium);
  const mount = composed[mountIdx];
  if (!mount) {
    return err(
      new CommandError(`${command}: no mount at index ${mountIdx} (pick a frame first?)`),
    );
  }
  const slot = mount.slots[slotIdx];
  if (!slot) {
    return err(new CommandError(`${command}: mount ${mountIdx} has no slot ${slotIdx}`));
  }
  return ok({ composed, provenance, mount, slot });
}

/** Named arguments for {@link writeComposedSlot}. */
export type WriteComposedSlotArgs = {
  loadout: LoadoutData;
  /** The composed array as resolved by {@link requireComposedSlot} for this same call. */
  composed: MountData[];
  /** The provenance array as resolved by {@link requireComposedSlot} for this same call. */
  provenance: (BonusMountProvenance | null)[];
  mountIdx: number;
  slotIdx: number;
  slot: SlotData;
}

/**
 * Write a slot at a composed index back to its dedicated storage: native
 * (`mountIdx < loadout.mounts.length`) or the bonus mount tail (`mountIdx >=
 * loadout.mounts.length`). `composed` must be the array `requireComposedSlot`
 * just resolved, so the mount's OTHER slot(s) (a multi-slot Flex mount) are
 * preserved when only one slot changes. `provenance` (same call, same index)
 * disambiguates WHICH bonus mount's dedicated storage field
 * (`improvedArmamentMount` vs `integratedWeaponMount`) a tail index writes to —
 * two bonus mounts can occupy the tail, so "every tail index is Improved Armament"
 * does not hold.
 *
 * **Native branch writes through the COMPOSED (slot-count-normalized) slots, never
 * the raw stored array**: a
 * stored Flex mount may still hold only 1 slot on disk (normalization pads
 * it to 2 only in the read-side `composed` array `requireComposedSlot` already
 * resolved) — mapping over `loadout.mounts[mountIdx].slots` directly (the old
 * `replaceSlot` helper) would silently no-op a write to slot 1, since `.map`
 * never grows the array. Taking `composed[mountIdx].slots` as the base fixes
 * that. The written-back mount keeps the **STORED** mount's own `type`
 * (`loadout.mounts[mountIdx].type`), never the composed mount's — a composed
 * native mount may carry a retrofit-in-place `type` override
 * (`applyRetrofitInPlace`) that is NOT real storage, and persisting it here
 * would break the untake-revert property (`loadoutPickLock.ts`'s `withCandidate`
 * doc-comment spells out the same rule for its own twin of this write).
 */
export function writeComposedSlot(args: WriteComposedSlotArgs): LoadoutData {
  const { loadout, composed, provenance, mountIdx, slotIdx, slot } = args;
  if (mountIdx < loadout.mounts.length) {
    const composedMount = composed[mountIdx];
    const storedMount = loadout.mounts[mountIdx];
    if (!composedMount || !storedMount) {
      // Unreachable via the reducer: see the identical note on the bonus-mount
      // branch below — callers only ever write an index `requireComposedSlot`
      // just resolved off the SAME loadout/pilot/compendium.
      return loadout;
    }
    const nextSlots = composedMount.slots.map((s, si) => (si === slotIdx ? slot : s));
    const nextMount: MountData = { ...storedMount, slots: nextSlots };
    return {
      ...loadout,
      mounts: loadout.mounts.map((m, mi) => (mi === mountIdx ? nextMount : m)),
    };
  }
  // Bonus-mount tail.
  const mount = composed[mountIdx];
  const prov = provenance[mountIdx];
  if (!mount || !prov) {
    // Unreachable via the reducer: callers only ever write an index they just
    // resolved through `requireComposedSlot` off the SAME `loadout`/`pilot`/
    // `compendium`, which would already have rejected a missing mount — and
    // `composedMounts`/`composedMountProvenance` derive from the same internal
    // walk, so a tail `mount` always has a matching `prov`. Defensive no-op
    // rather than a throw, since this is a write helper, not a gate.
    return loadout;
  }
  const nextMount: MountData = {
    ...mount,
    slots: mount.slots.map((s, si) => (si === slotIdx ? slot : s)),
  };
  return { ...loadout, [prov.storageField]: nextMount };
}

/**
 * The fitting size of a slot, derived from its mount's frame mount-type string
 * (e.g. "Main/Aux") via {@link slotSizesForMount}, indexed by slot position. The
 * mount was materialized from a known type at `select_frame` time, so `sizes`
 * exists and is the right length; default defensively if the data drifts.
 */
export function slotFittingSizeOf(mountType: string, slotIdx: number): string {
  const sizes = slotSizesForMount(mountType);
  return sizes?.[slotIdx] ?? "Auxiliary";
}
